import { METRIC_CATEGORY_ORDER, MetricCategory } from '@chiroup/core/constants/metricCategories';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import React, { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import useEventLogClinicDaily from '../../hooks/useEventLogClinicDaily';
import useEventLogClinicSummary from '../../hooks/useEventLogClinicSummary';
import useEventLogUsers from '../../hooks/useEventLogUsers';
import Header from '../layout/Header';
import CategoryTabs from './CategoryTabs';
import DateRangePicker, { computeRange, RangePreset } from './DateRangePicker';
import MetricChartModal from './MetricChartModal';
import MetricDrawer from './MetricDrawer';
import MetricGrid from './MetricGrid';
import SummaryCard from './SummaryCard';
import UserFilterDropdown from './UserFilterDropdown';

const VALID_PRESETS = new Set<RangePreset>(['7d', '30d', '90d', 'custom']);
const VALID_CATEGORIES = new Set<MetricCategory>(METRIC_CATEGORY_ORDER);

const parseUserIds = (raw: string | null): string[] => {
  if (!raw) return [];

  return raw
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);
};

const EventLogClinicDetail: React.FC = () => {
  const { clinicId } = useParams<{ clinicId: string }>();
  const id = Number(clinicId);

  const [searchParams, setSearchParams] = useSearchParams();
  const rangeFromUrl = (searchParams.get('range') as RangePreset) || '7d';
  const range: RangePreset = VALID_PRESETS.has(rangeFromUrl) ? rangeFromUrl : '7d';
  const tabFromUrl = (searchParams.get('tab') as MetricCategory) || 'clinical';
  const activeTab: MetricCategory = VALID_CATEGORIES.has(tabFromUrl) ? tabFromUrl : 'clinical';
  const userIds = parseUserIds(searchParams.get('users'));
  const userIdsParam = userIds.length ? userIds.join(',') : undefined;
  const customStart = searchParams.get('start') || undefined;
  const customEnd = searchParams.get('end') || undefined;

  const { startDate, endDate } = useMemo(
    () => computeRange(range, customStart, customEnd),
    [range, customStart, customEnd]
  );

  const [drawerMetric, setDrawerMetric] = useState<string | null>(null);
  const [expandedMetric, setExpandedMetric] = useState<string | null>(null);

  const { data: summary, isFetching: summaryLoading } = useEventLogClinicSummary(
    id,
    startDate,
    endDate,
    userIdsParam
  );
  const { data: daily, isFetching: dailyLoading } = useEventLogClinicDaily(
    id,
    startDate,
    endDate,
    userIdsParam
  );
  const { data: usersData, isFetching: usersLoading } = useEventLogUsers(id, startDate, endDate);

  const updateParams = (updates: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(updates)) {
      if (value == null || value === '') {
        next.delete(key);
      } else {
        next.set(key, value);
      }
    }
    setSearchParams(next, { replace: false });
  };

  const onChangeRange = (preset: RangePreset, start?: string, end?: string) => {
    if (preset === 'custom' && start && end) {
      updateParams({ range: 'custom', start, end });
    } else {
      updateParams({ range: preset, start: null, end: null });
    }
  };

  const onChangeTab = (next: MetricCategory) => updateParams({ tab: next });
  const onChangeUsers = (ids: string[]) => {
    updateParams({ users: ids.length ? ids.join(',') : null });
  };

  const filteredUserNames = useMemo(() => {
    if (!userIds.length || !usersData) return undefined;
    const names = userIds
      .map(uid => usersData.find(u => u.userId === uid)?.userName)
      .filter(Boolean) as string[];

    return names.length ? names : undefined;
  }, [userIds, usersData]);

  const filteredLabel = filteredUserNames
    ? filteredUserNames.length <= 2
      ? filteredUserNames.join(', ')
      : `${filteredUserNames.length} users`
    : undefined;

  return (
    <>
      <Header
        title={
          <div className="flex items-center gap-3">
            <Link to="/event-logs" className="text-gray-400 hover:text-gray-600">
              <ArrowLeftIcon className="h-5 w-5" />
            </Link>
            <span>{summary?.clinic?.name || `Clinic ${clinicId}`}</span>
          </div>
        }
        rightSide={
          <div className="flex items-center gap-2">
            <UserFilterDropdown
              users={usersData || []}
              selectedUserIds={userIds}
              onChange={onChangeUsers}
              loading={usersLoading && !usersData?.length}
            />
            <DateRangePicker
              active={range}
              customStart={customStart}
              customEnd={customEnd}
              onChange={onChangeRange}
            />
          </div>
        }
      />

      <div className="p-6 space-y-6">
        <SummaryCard
          summary={summary}
          daily={daily}
          loading={summaryLoading || dailyLoading}
          filteredUserName={filteredLabel}
        />

        <div>
          <CategoryTabs
            active={activeTab}
            onChange={onChangeTab}
            categoryTotals={
              summary?.categoryTotals || {
                schedule: 0,
                clinical: 0,
                billing: 0,
                patients: 0,
                databases: 0,
                clinic_settings: 0,
                user_settings: 0,
                resources: 0,
              }
            }
          />
          <div className="pt-5">
            <MetricGrid
              category={activeTab}
              metrics={summary?.metrics || {}}
              previousMetrics={summary?.previousMetrics || {}}
              dailyByMetric={daily?.dailyByMetric || {}}
              onMetricClick={setDrawerMetric}
              onMetricExpand={setExpandedMetric}
              loading={summaryLoading && !summary}
            />
          </div>
        </div>
      </div>

      <MetricDrawer
        open={!!drawerMetric}
        onClose={() => setDrawerMetric(null)}
        clinicId={id}
        metricKey={drawerMetric}
        startDate={startDate}
        endDate={endDate}
        userId={userIdsParam}
      />

      <MetricChartModal
        open={!!expandedMetric}
        onClose={() => setExpandedMetric(null)}
        metricKey={expandedMetric}
        value={(summary?.metrics || {})[expandedMetric || ''] ?? 0}
        previousValue={(summary?.previousMetrics || {})[expandedMetric || ''] ?? 0}
        dailySeries={expandedMetric ? (daily?.dailyByMetric || {})[expandedMetric] : undefined}
        dates={daily?.dates}
        granularity={daily?.granularity}
      />
    </>
  );
};

export default EventLogClinicDetail;
